import { useState } from 'react';
import { ExternalLink, FileSignature } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  SubBar,
} from '@/design/components';
import { useMe } from '@/auth/useMe';
import { errorMessage } from '@/lib/errors';
import { formatDateTime } from '@/lib/dates';
import { PrivateDetails } from '@/features/employees/private/PrivateDetails';
import { SignaturePad } from '@/features/employees/private/SignaturePad';
import {
  contractStatusLabel,
  contractStatusTone,
  uploadHrDoc,
  useHrDocUrl,
  useMyContracts,
  useSignContract,
  type Contract,
} from '@/features/employees/private/api';

/**
 * המסמכים שלי: חוזים לחתימה, ת"ז ופרטי בנק (מיגרציה 092).
 *
 * העובד ממלא את זה בעצמו. זו גם הסיבה שהמסך הזה קיים: טופס קליטה
 * שמנהל ממלא בשביל עובד הוא טופס שמוקלד פעמיים — פעם בנייר ופעם
 * במערכת — ובדרך מתחלפות ספרות בחשבון בנק.
 */
export function MyDocumentsPage() {
  const { me } = useMe();
  const contracts = useMyContracts(me?.employeeId);

  if (!me) return null;

  const pending = (contracts.data ?? []).filter((contract) => contract.status === 'pending');
  const rest = (contracts.data ?? []).filter((contract) => contract.status !== 'pending');

  return (
    <div className="grid content-start">
      <SubBar title="המסמכים שלי" subtitle="חוזים לחתימה, תעודת זהות ופרטי בנק" />

      <div className="grid gap-6 p-4">
        <section className="grid gap-3">
          <h2 className="m-0 font-display text-display-s font-semibold text-ink">חוזים</h2>

          {contracts.isPending && <Skeleton className="h-24" />}
          {contracts.isError && <ErrorState onRetry={() => void contracts.refetch()} />}
          {contracts.data?.length === 0 && (
            <EmptyState
              title="אין חוזים"
              description="כשיישלח אליך חוזה לחתימה הוא יופיע כאן."
            />
          )}

          {pending.map((contract) => (
            <SignCard key={contract.id} contract={contract} employeeId={me.employeeId} />
          ))}

          {rest.map((contract) => (
            <DoneCard key={contract.id} contract={contract} />
          ))}
        </section>

        <section className="grid gap-3">
          <h2 className="m-0 font-display text-display-s font-semibold text-ink">
            תעודת זהות ופרטי בנק
          </h2>
          <PrivateDetails employeeId={me.employeeId} />
        </section>
      </div>
    </div>
  );
}

function SignCard({ contract, employeeId }: { contract: Contract; employeeId: string }) {
  const file = useHrDocUrl(contract.file_path);
  const sign = useSignContract();

  const [signature, setSignature] = useState<Blob | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="grid gap-3 rounded-md border border-line bg-raised p-3">
      <div className="flex flex-wrap items-center gap-2">
        <FileSignature size={18} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        <span className="text-title font-medium">{contract.title}</span>
        <StatusBadge tone={contractStatusTone[contract.status]}>
          {contractStatusLabel[contract.status]}
        </StatusBadge>
      </div>

      {contract.body && (
        <div className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-sm border border-line bg-surface p-3 text-body text-ink">
          {contract.body}
        </div>
      )}

      {file.data && (
        <a
          href={file.data}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-body-sm text-ink"
        >
          <ExternalLink size={14} strokeWidth={1.75} aria-hidden="true" />
          פתיחת הקובץ המצורף
        </a>
      )}

      <div className="grid gap-1">
        <label htmlFor={`name-${contract.id}`} className="text-label font-medium">
          השם המלא שלי
        </label>
        <input
          id={`name-${contract.id}`}
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
        />
      </div>

      <div className="grid gap-1">
        <span className="text-label font-medium">החתימה שלי</span>
        <SignaturePad onChange={setSignature} />
      </div>

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <p className="m-0 text-body-sm text-muted">
        החתימה והשעה נשמרות יחד עם הנוסח שלמעלה, והנוסח נעול לשינוי מרגע החתימה.
      </p>

      <div>
        <Button
          variant="primary"
          disabled={busy || sign.isPending || signature === null || name.trim() === ''}
          onClick={() => {
            if (!signature) return;
            setError(null);
            setBusy(true);
            uploadHrDoc(employeeId, 'signature', signature, 'image/png')
              .then((path) =>
                sign.mutateAsync({
                  contractId: contract.id,
                  signaturePath: path,
                  name: name.trim(),
                }),
              )
              .catch((caught: unknown) => setError(errorMessage(caught)))
              .finally(() => setBusy(false));
          }}
        >
          חתימה ושליחה
        </Button>
      </div>
    </div>
  );
}

function DoneCard({ contract }: { contract: Contract }) {
  const file = useHrDocUrl(contract.file_path);

  return (
    <div className="grid gap-2 rounded-md border border-line bg-raised p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body font-medium">{contract.title}</span>
        <StatusBadge tone={contractStatusTone[contract.status]}>
          {contractStatusLabel[contract.status]}
        </StatusBadge>
      </div>
      {contract.signed_at && (
        <p className="m-0 text-body-sm text-muted">נחתם ב{formatDateTime(contract.signed_at)}</p>
      )}
      {file.data && (
        <a
          href={file.data}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-body-sm text-ink"
        >
          <ExternalLink size={14} strokeWidth={1.75} aria-hidden="true" />
          הקובץ המצורף
        </a>
      )}
    </div>
  );
}
